import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { NEW_WEBHOOK_PAYLOAD_VERSION } from "@signalist/shared-types";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { useCreateWebhookMutation } from "@/pages/Private/redux/webhook/webhook.api";
import {
	CreateWebhook,
	CreateWebhookSchema,
	WebhookCreated,
} from "@/pages/Private/pages/Company/pages/WebhookOverview/schema/webhook";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";
import { ButtonSize } from "@/components/Button/types";
import { Button } from "@/components/Button/Button";

import { WebhookPayloadVersionField } from "./WebhookPayloadVersionField";

interface createWebhookModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	/** Receives the created webhook with its signing secret, to show it once (PREC-4249). */
	onCreated?: (webhook: WebhookCreated) => void;
}

export const CreateWebhookModal: FunctionComponent<createWebhookModalProps> = ({
	isOpen,
	setIsOpen,
	onCreated,
}) => {
	const { t } = useTranslation();
	const [createWebhook, { isLoading, error }] = useCreateWebhookMutation();

	const [customColumn, setCustomColumn] = useState<string>();

	const [currentFormState, setCurrentFormState] = useState<CreateWebhook>({
		name: "",
		url: "",
		receivesData: false,
		customColumns: [],
		payloadVersion: NEW_WEBHOOK_PAYLOAD_VERSION,
	});

	const handleSaveFormState = (
		value: CreateWebhook[keyof CreateWebhook],
		name: keyof CreateWebhook
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateWebhook>({
		defaultValues: {
			name: "",
			url: "",
			receivesData: false,
			customColumns: [],
			payloadVersion: NEW_WEBHOOK_PAYLOAD_VERSION,
		},
		resolver: zodResolver(CreateWebhookSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateWebhook) => {
		try {
			const created = await createWebhook(values).unwrap();

			setIsOpen(false);
			onCreated?.(created);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={"Create Webhook"}
		>
			<div>
				<div className="flex items-center mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							handleSaveFormState(e.target.value, "name");
						}}
						label={"Name"}
						name={"name"}
						placeholder={"e.g. My Webhook"}
						value={currentFormState.name}
					/>
				</div>

				<div className="flex items-center mb-1">
					<InputField
						error={!!errors.url?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							handleSaveFormState(e.target.value, "url");
						}}
						label={"Webhook URL"}
						name={"url"}
						placeholder={"e.g. https://example.com/webhook"}
						value={currentFormState.url}
					/>
				</div>

				<WebhookPayloadVersionField
					value={currentFormState.payloadVersion === 1 ? 1 : 2}
					onChange={(version) => handleSaveFormState(version, "payloadVersion")}
				/>

				<div className="flex items-center gap-2 mb-4">
					<Toggle
						isChecked={currentFormState.receivesData}
						name={"receivesData"}
						onChange={() => {
							handleSaveFormState(!currentFormState.receivesData, "receivesData");
						}}
					/>
					<div className="text-sm text-text-meta">Receives data after workflow is completed</div>
				</div>

				{currentFormState.receivesData && (
					<>
						<div className="text-sm text-text-meta mb-4">
							When the workflow that is connected to this webhook will write back to signalist you
							need to define into which columns you want to write back
						</div>
						<div className="flex flex-row items-start space-y-4">
							<div className="text-ssm font-medium !mb-[-12px] grow">
								<InputField
									className="!mb-0 grow mr-4"
									error={!!errors.customColumns}
									handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
										setCustomColumn(event.target.value);
									}}
									name={"customColumns"}
									placeholder="Enter Column Name"
									value={customColumn ?? ""}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											if (customColumn && !currentFormState.customColumns?.includes(customColumn)) {
												handleSaveFormState(
													[...(currentFormState.customColumns ?? []), customColumn],
													"customColumns"
												);
											}

											setCustomColumn("");
										}
									}}
								/>
							</div>
							<div className="!mt-0 ml-4 flex items-start">
								<Button
									className="!mt-0"
									disabled={!customColumn || currentFormState.customColumns?.includes(customColumn)}
									image={<Icon className="mr-2" icon={faPlus} />}
									size={ButtonSize.L}
									title="Add Column"
									onClick={() => {
										if (customColumn && !currentFormState.customColumns?.includes(customColumn)) {
											handleSaveFormState(
												[...(currentFormState.customColumns ?? []), customColumn],
												"customColumns"
											);
											setCustomColumn("");
										}
									}}
								/>
							</div>
						</div>
						{customColumn && currentFormState.customColumns?.includes(customColumn) ? (
							<div className="text-xs text-text-meta mb-2 mt-1">Column names need to be unique</div>
						) : (
							""
						)}
						<div>
							{(currentFormState?.customColumns ?? []).length > 0 && (
								<div className="text-ssm font-medium mt-4 mb-2">Column names</div>
							)}
							{(currentFormState?.customColumns ?? []).map((value, index) => (
								<div
									key={index}
									className="flex items-center justify-between h-[54px] px-2.5 py-4.5 rounded-xl border border-gray-200 mb-4"
								>
									<div>{value}</div>
									<button
										className="cursor-pointer"
										onClick={() => {
											const values = [...(currentFormState.customColumns ?? [])];

											values.splice(index, 1);
											handleSaveFormState(values, "customColumns");
										}}
									>
										<img alt="trash" src={trashIcon} />
									</button>
								</div>
							))}
						</div>
					</>
				)}

				<div className="text-sm text-text-meta mb-4" data-test="webhook-create-signing-note">
					{t("webhookSigning.createNote")}
				</div>

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
		</Modal>
	);
};
